<template>
  <component
    :is="componentType"
    :id="tagId"
    v-editable="blok"
    :class="[
      'text-animated',
      customMarginClass,
      paragraphBoldClass,
      fontStyleClass,
      textColorClass,
      narrowTextClass,
      positionMobileClass,
      positionTabletClass,
      positionClass,
    ]"
    :style="[maxWidth]"
  >
    <span v-html="decodeAndReplaceText(blok.text || '')" />
    <span
      v-if="animatedWords.length > 0"
      class="text-animated__mask"
      :class="[animatedFontStyleClass]"
      :style="{ color: animatedColor, width: maskWidth }"
    >
      <span
        v-for="(word, i) in animatedWords"
        :key="word"
        :class="['text-animated__word', animatedFontStyleClass]"
        :data-show="i === currentWordIndex || null"
        :data-up="i === upWordIndex || null"
      >
        {{ word }}
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import type { SbSimpleTextStoryblok } from '@/types'
import { slugify } from '@/utils/helpers'

interface Props {
  blok: SbSimpleTextStoryblok
}
const { decodeAndReplaceText } = useTextModifiers()
const props = defineProps<Props>()
const animatedWords = ref<string[]>(props.blok.animated_text?.split(', ') || [])
const intervalId = ref<ReturnType<typeof setTimeout>>()
const currentWordIndex = ref<number | null>(null)
const upWordIndex = ref<number | null>(null)

const mainText = computed(() => decodeAndReplaceText(props.blok.text || ''))
const componentType = computed(() => props.blok.tag_type || 'p')
const paragraphBoldClass = computed(
  () => props.blok.is_bold && 'paragraph--bold',
)
const customMarginClass = computed(
  () => `mb-${props.blok?.custom_margin || ''}`,
)
const fontStyleClass = computed(() => props.blok.font?.font?.value ?? '')
const textColorClass = computed(() =>
  props.blok?.font?.color_name ? `text-${props.blok.font.color_name}` : 'none',
)
const narrowTextClass = computed(() => (props.blok.narrow ? 'text-narrow' : ''))
const positionClass = computed(
  () => `text-position--${props.blok.position}` || 'text-position--left',
)
const positionTabletClass = computed(
  () => `text-position-tablet--${props.blok.position_tablet}` || null,
)
const positionMobileClass = computed(
  () => `text-position-mobile--${props.blok.position_mobile}` || null,
)
const maxWidth = computed(() => {
  if (props.blok?.max_width) {
    return {
      maxWidth: `${props.blok.max_width}px`,
    }
  }
  return {
    maxWidth: 'none',
  }
})
const tagId = computed(() => {
  if (props.blok.tag_type === 'h2' || props.blok.tag_type === 'h3') {
    return slugify(decodeAndReplaceText(props.blok.text as string))
  }
  return null
})

const animatedFontStyleClass = computed(
  () => props.blok.animated_font?.font?.value ?? '',
)
const animatedColor = computed(() => {
  return props.blok.animated_color?.value || 'var(--blue-darker-1)'
})
const maskWidth = computed(() => {
  if (animatedWords.value.length === 0) return '0px'
  const longestWord = animatedWords.value.reduce((a, b) =>
    a.length > b.length ? a : b,
  )
  return `${longestWord.length * 0.6}em`
})

const updateAnimation = () => {
  if (animatedWords.value.length <= 1 || currentWordIndex.value === null) {
    return
  }
  const nextIndex = (currentWordIndex.value + 1) % animatedWords.value.length
  upWordIndex.value = currentWordIndex.value
  currentWordIndex.value = nextIndex
}

onMounted(() => {
  if (animatedWords.value.length > 0) {
    if (animatedWords.value.length === 1) {
      currentWordIndex.value = 0
    } else {
      upWordIndex.value = 0
      currentWordIndex.value = 1
      intervalId.value = setInterval(updateAnimation, 2000)
    }
  }
})

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
})
</script>

<style lang="scss" scoped>
.text-animated {
  @media (min-width: $breakpoint-md) {
    max-width: 100%;
  }
  &__mask {
    height: 1lh;
    line-height: inherit;
    overflow: hidden;
    position: relative;
    display: inline-block;
    vertical-align: bottom;

    @supports not (height: 1lh) {
      height: 1.2em;
    }
  }
  &__word {
    position: absolute;
    top: 1lh;
    left: 0;
    right: 0;
    white-space: nowrap;

    @supports not (top: 1lh) {
      top: 1.2em;
    }
  }
  &__word[data-show] {
    transform: translateY(-100%);
    transition: 0.5s transform ease-in-out;
  }
  &__word[data-up] {
    transform: translateY(-200%);
    transition: 0.5s transform ease-in-out;
  }
}

// Styling props from SbSimpleText
.text-pill {
  &::after {
    display: inline-flex;
    transform: translateY(-10%);
    content: attr(data-visual-label);
    background-color: var(--cobalt-800);
    padding: 2px 8px;
    border-radius: 50vh;
    color: var(--white);
    font-size: 11px;
    line-height: 16px;
    margin-left: 8px;
    text-transform: uppercase;
    align-items: center;
    justify-content: center;
    font-style: normal;
    font-weight: 600;
    width: fit-content;
  }
}

.text-narrow {
  max-width: 660px;
  margin: 0 auto;
}

.text-position {
  margin-left: 0;
  margin-right: 0;

  &--center {
    text-align: center;
  }

  &--left {
    text-align: left;
  }

  &--right {
    text-align: right;
  }
}

.text-position-tablet {
  &--center {
    @media (max-width: $breakpoint-md) {
      text-align: center;
    }
  }

  &--left {
    @media (max-width: $breakpoint-md) {
      text-align: left;
    }
  }

  &--right {
    @media (max-width: $breakpoint-md) {
      text-align: right;
    }
  }
}

.text-position-mobile {
  &--center {
    @media (max-width: $breakpoint-lg) {
      text-align: center;
    }
  }

  &--left {
    @media (max-width: $breakpoint-lg) {
      text-align: left;
    }
  }

  &--right {
    @media (max-width: $breakpoint-lg) {
      text-align: right;
    }
  }
}
</style>
